import type { UploadedFile } from '@elkaso-app/web-design';
import {
  Badge,
  Box,
  BoxProps,
  Button,
  ChakraSelect,
  FileUploader,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButtonWithTooltip,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import {
  SupplierDocument,
  supplierDocumentsQueryKey,
  SupplierDocumentType,
  SUPPLIER_DOCUMENT_TYPE_OPTIONS,
  useDeleteSupplierDocumentApi,
  useGetSupplierDocumentsApi,
  useUploadSupplierDocumentApi,
} from 'apis/supplier-documents';
import { SupplierProfileDto } from 'apis/supplier-profiles';
import dayjs from 'dayjs';
import {
  DOCUMENT_EXPIRY_META,
  getDocumentExpiryStatus,
  getDocumentLabel,
} from 'pages/supplier-profiles/utils/document-expiry';
import {
  DOCUMENT_ACCEPT_TYPES,
  formatFileSize,
  MAX_DOCUMENT_FILE_SIZE,
} from 'pages/supplier-profiles/utils/document-files';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

type TInputs = {
  type: SupplierDocumentType | '';
  customLabel: string;
  expiryDate: string;
};

const defaultValues: TInputs = { type: '', customLabel: '', expiryDate: '' };

export interface ISupplierDocumentsModalCell extends BoxProps {
  trigger: JSX.Element;
  profile: SupplierProfileDto;
}

/** Compliance documents held against a supplier profile — trade licence, VAT certificate and
 * anything else ops is asked for.
 *
 * Supplier Profiles has no detail page, so this follows the module's row-action-modal pattern
 * (see notification-settings-modal.tsx) rather than introducing a route. Replacing a document
 * is upload-then-delete: renewals legitimately coexist with their predecessors, and the expiry
 * badge is what tells them apart. */
export const SupplierDocumentsModal = ({ trigger, profile }: ISupplierDocumentsModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const { data: documents, isLoading } = useGetSupplierDocumentsApi(isOpen ? profile.id : null);
  const { mutate: uploadDocument, isLoading: isUploading } = useUploadSupplierDocumentApi({
    onSuccess: () => {
      reset(defaultValues);
      setFile(null);
    },
  });
  const { mutate: deleteDocument, isLoading: isDeleting } = useDeleteSupplierDocumentApi();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TInputs>({ defaultValues });

  const selectedType = useWatch({ control, name: 'type' });

  useEffect(() => {
    if (!isOpen) return;
    reset(defaultValues);
    setFile(null);
    // The urls are presigned (~15 min); invalidating on open means a link is never
    // served from a stale cache, which would fail with an S3 XML error users read
    // as "the document is broken".
    queryClient.invalidateQueries([supplierDocumentsQueryKey, profile.id]);
  }, [isOpen]);

  const onSubmit = (values: TInputs) => {
    if (!file || !values.type) return;
    uploadDocument({
      supplierProfileId: profile.id,
      type: values.type,
      customLabel: values.customLabel.trim() || undefined,
      expiryDate: values.expiryDate || undefined,
      file,
    });
  };

  const rows = documents ?? [];

  return (
    <>
      <Box onClick={onOpen}>{trigger}</Box>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '3xl' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Documents — {profile.businessName}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack spacing='lg'>
              <Stack spacing='sm'>
                <SectionHeading>On file</SectionHeading>

                {isLoading ? (
                  <HStack justify='center' py='lg'>
                    <Spinner size='sm' />
                  </HStack>
                ) : rows.length === 0 ? (
                  <Text fontSize='sm' color='gray.500'>
                    No documents yet — add the trade licence and VAT registration certificate below.
                  </Text>
                ) : (
                  <Stack spacing='xs'>
                    {rows.map((document) => (
                      <DocumentRow
                        key={document.id}
                        document={document}
                        isDeleting={isDeleting}
                        onDelete={() => deleteDocument({ supplierProfileId: profile.id, documentId: document.id })}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>

              <Stack spacing='md'>
                <SectionHeading>Add a document</SectionHeading>

                <Controller
                  control={control}
                  name='type'
                  rules={{ required: 'Choose what this document is' }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <FormControl isRequired isInvalid={!!error}>
                      <FormLabel>Type</FormLabel>
                      <ChakraSelect
                        options={SUPPLIER_DOCUMENT_TYPE_OPTIONS}
                        value={SUPPLIER_DOCUMENT_TYPE_OPTIONS.find((option) => option.value === value) ?? null}
                        onChange={(option: any) => onChange(option?.value ?? '')}
                        onBlur={onBlur}
                        placeholder='Select document type'
                      />
                      <FormErrorMessage>{error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                {selectedType === 'OTHER' && (
                  <FormControl isRequired isInvalid={!!errors.customLabel}>
                    <FormLabel>Label</FormLabel>
                    <Input
                      placeholder='e.g. Chamber of Commerce certificate'
                      {...register('customLabel', {
                        validate: (value) => value.trim().length > 0 || 'Name the document so it is recognisable',
                      })}
                    />
                    <FormErrorMessage>{errors.customLabel?.message}</FormErrorMessage>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Expiry date</FormLabel>
                  <Input type='date' {...register('expiryDate')} />
                  <FormHelperText>Leave empty for documents that do not expire.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>File</FormLabel>
                  <FileUploader
                    style='style_01'
                    message='Drop the document here or browse — PDF or image'
                    acceptedTypes={DOCUMENT_ACCEPT_TYPES}
                    maxFiles={1}
                    // Explicit — FileUploader defaults to 5MB, below the BE's 10MB limit.
                    maxFileSize={MAX_DOCUMENT_FILE_SIZE}
                    onRemove={() => setFile(null)}
                    onRemoveAll={() => setFile(null)}
                    // The component may hand back the whole selection or only the new file;
                    // maxFiles={1} means the last one is always the one ops meant.
                    onUpload={(uploaded: UploadedFile[]) => setFile(uploaded[uploaded.length - 1] ?? null)}
                  />
                  {file && (
                    <Text fontSize='sm' color='gray.600' mt='xs' isTruncated>
                      {file.name}
                    </Text>
                  )}
                </FormControl>
              </Stack>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={onClose}>
                Close
              </Button>
              <Button type='submit' colorScheme='red' isLoading={isUploading} isDisabled={!file}>
                Add document
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

/** Same xs uppercase gray + hairline rule as the edit modal's sections. */
const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <HStack spacing='sm' align='center'>
    <Text
      fontSize='xs'
      fontWeight='semibold'
      color='gray.500'
      textTransform='uppercase'
      letterSpacing='0.4px'
      whiteSpace='nowrap'>
      {children}
    </Text>
    <Box flex='1' h='1px' bg='gray.100' />
  </HStack>
);

const DocumentRow = ({
  document,
  isDeleting,
  onDelete,
}: {
  document: SupplierDocument;
  isDeleting: boolean;
  onDelete: () => void;
}) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const expiry = DOCUMENT_EXPIRY_META[getDocumentExpiryStatus(document.expiryDate)];

  return (
    <HStack
      spacing='sm'
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
      rowGap='xs'
      border='1px solid'
      borderColor='gray.100'
      borderRadius='md'
      px='sm'
      py='xs'>
      {/* The base minW forces the badge/actions onto their own wrapped row on phones instead of
          crushing the file name to zero width. */}
      <Stack spacing='0' flex='1' minW={{ base: '180px', md: '0' }}>
        <Text fontSize='sm' fontWeight='semibold' isTruncated>
          {getDocumentLabel(document)}
        </Text>
        <Text fontSize='xs' color='gray.500' isTruncated>
          {document.fileName} · {formatFileSize(document.fileSize)} · added{' '}
          {dayjs(document.createdAt).format('DD MMM YYYY')}
        </Text>
      </Stack>

      <Stack spacing='0' align='flex-end' minW={{ base: '0', md: '120px' }} flexShrink={0}>
        <Badge color={expiry.color} bgColor={expiry.bg} px='sm' borderRadius='full' fontSize='xs'>
          {expiry.label}
        </Badge>
        {document.expiryDate && (
          <Text fontSize='xs' color='gray.400' whiteSpace='nowrap'>
            {dayjs(document.expiryDate).format('DD MMM YYYY')}
          </Text>
        )}
      </Stack>

      {/* Two-step: a compliance document is tedious to obtain again, so a stray
          click on a small icon must not be enough to remove one. */}
      {isConfirmingDelete ? (
        <HStack spacing='xs' flexShrink={0}>
          <Button size='xs' variant='ghost' onClick={() => setIsConfirmingDelete(false)}>
            Cancel
          </Button>
          <Button size='xs' colorScheme='red' isLoading={isDeleting} onClick={onDelete}>
            Remove
          </Button>
        </HStack>
      ) : (
        <HStack spacing='2px' flexShrink={0}>
          <IconButtonWithTooltip
            size='sm'
            variant='unstyled'
            label='Download'
            aria-label='download-document-btn'
            Icon={<FiDownload />}
            onClick={() => window.open(document.url, '_blank', 'noopener,noreferrer')}
          />
          <IconButtonWithTooltip
            size='sm'
            variant='unstyled'
            label='Remove'
            aria-label='delete-document-btn'
            Icon={<FiTrash2 color='#E53E3E' />}
            onClick={() => setIsConfirmingDelete(true)}
          />
        </HStack>
      )}
    </HStack>
  );
};
